import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiURL from '../Api/Api';
import { fetchJson } from '../utils/http';

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const typeClasses = {
  credit: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  debit: 'bg-rose-50 text-rose-700 border-rose-200',
  return: 'bg-amber-50 text-amber-700 border-amber-200',
};

const TransactionHistoryReport = ({ transactionNo, userId, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && transactionNo && userId) {
      fetchHistory();
    } else {
      setHistory([]);
      setIsLoading(false);
    }
  }, [isOpen, transactionNo, userId]);

  const fetchHistory = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchJson(`${ApiURL}/getTransactionHistory.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, transaction_no: transactionNo }),
      });

      if (data.status === 'success' && Array.isArray(data.transactions)) {
        setHistory(data.transactions);
      } else {
        setError(data.message || 'Invalid transaction history data received');
      }
    } catch (fetchError) {
      setError(`Error fetching transaction history: ${fetchError.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="mt-2 mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-md"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-blue-600">
              Transaction {transactionNo || 'N/A'} History
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Original transaction snapshot and latest updated record
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Close
          </button>
        </div>

        {isLoading ? (
          <div className="py-4 text-center text-[10px] text-blue-600 sm:text-sm">
            Loading history...
          </div>
        ) : error ? (
          <div className="py-4 text-center text-[10px] text-red-600 sm:text-sm">
            Error: {error}
          </div>
        ) : history.length === 0 ? (
          <div className="py-4 text-center text-[10px] text-blue-600 sm:text-sm">
            No history found for Transaction {transactionNo || 'N/A'}
          </div>
        ) : (
          <div className="relative max-h-[50vh] w-full overflow-x-auto overflow-y-auto">
            <table className="w-full overflow-hidden rounded-lg text-left text-[10px] text-gray-700 shadow-md sm:text-xs">
              <thead className="sticky top-0 z-10 bg-blue-200 text-[10px] uppercase text-black shadow-sm sm:text-xs">
                <tr>
                  <th className="whitespace-nowrap px-2 py-1.5">Serial No</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Txn No</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Version</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Date</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Type</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Credit</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Debit</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Net Balance</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Particular To</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Site</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Project</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Main Head</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Sub Head</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Remarks</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Status</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Created By</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Created At</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Updated By</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Updated At</th>
                  <th className="whitespace-nowrap px-2 py-1.5">Edit Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {history.map((txn, index) => (
                  <tr
                    key={`${txn.transaction_no}-${txn.history_id}`}
                    className={`border-b transition-colors duration-200 ${
                      txn.version_type === 'updated'
                        ? 'bg-emerald-50 hover:bg-emerald-100'
                        : 'bg-white hover:bg-blue-50'
                    }`}
                  >
                    <td className="px-2 py-1.5">{index + 1}</td>
                    <td className="px-2 py-1.5">{txn.transaction_no || '-'}</td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          txn.version_type === 'updated'
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            : 'border-slate-200 bg-slate-50 text-slate-600'
                        }`}
                      >
                        {txn.version_type === 'updated' ? 'Updated' : 'Original'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">{txn.transaction_date || '-'}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${typeClasses[txn.amount_type] || 'border-slate-200 bg-slate-50 text-slate-700'}`}>
                        {txn.amount_type || '-'}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">₹{txn.credit_amount || '0.00'}</td>
                    <td className="px-2 py-1.5">₹{txn.debit_amount || '0.00'}</td>
                    <td className="px-2 py-1.5">₹{txn.net_balance || '0.00'}</td>
                    <td className="px-2 py-1.5">{txn.particuler_to || '-'}</td>
                    <td className="px-2 py-1.5">{txn.site || '-'}</td>
                    <td className="px-2 py-1.5">{txn.project_name || '-'}</td>
                    <td className="px-2 py-1.5">{txn.main_head || '-'}</td>
                    <td className="px-2 py-1.5">{txn.sub_head || '-'}</td>
                    <td className="px-2 py-1.5">{txn.remarks || '-'}</td>
                    <td className="px-2 py-1.5">{txn.form_status || '-'}</td>
                    <td className="px-2 py-1.5 text-purple-600">{txn.created_by || '-'}</td>
                    <td className="px-2 py-1.5 text-purple-600">{formatDateTime(txn.created_at)}</td>
                    <td className="px-2 py-1.5 text-green-600">{txn.updatedBy || txn.updated_by || '-'}</td>
                    <td className="px-2 py-1.5 text-green-600">{formatDateTime(txn.updatedDate || txn.updated_at)}</td>
                    <td className="px-2 py-1.5">{formatDateTime(txn.edit_timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pointer-events-none absolute top-0 right-0 h-full w-3 bg-gradient-to-l from-gray-200/50 to-transparent sm:hidden"></div>
          </div>
        )}

        <style jsx>{`
          .overflow-x-auto {
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          .overflow-y-auto {
            max-height: 50vh;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          @media (max-width: 480px) {
            .overflow-x-auto {
              max-width: 100vw;
            }
            table {
              min-width: 1200px;
              width: auto;
            }
          }
          th {
            white-space: nowrap;
          }
        `}</style>
      </motion.div>
    </AnimatePresence>
  );
};

export default TransactionHistoryReport;
